import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { colors as dsColors, mediaQuery } from '@modernhealth/vital-foundation';
import { useMediaQuery } from '@react-hook/media-query';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import styled from '@emotion/styled';
import { Icon as DsIcon } from '../Icon/Icon';
import { SkeletonPlaceholder } from '../Skeleton/SkeletonPlaceholder';
import { DeprecatedReactRouterLink as Link } from '../Link/DeprecatedReactRouterLink';
import { BodyTextSmall, BodyTextXSmall, HeadingText10, HeadingText30, } from '../Typography/Typography';
import { pxToRem } from '../../helpers/pxToRem';
export var ContentCardTestId;
(function (ContentCardTestId) {
    ContentCardTestId["Completed"] = "ContentCardTestId.Completed";
    ContentCardTestId["Locked"] = "ContentCardTestId.Locked";
})(ContentCardTestId || (ContentCardTestId = {}));
// the colors are duplicated so we can assign them to numbers from 0 to 9
const possibleColors = [
    dsColors.yellow05,
    dsColors.orange05,
    dsColors.blue20,
    dsColors.cyan05,
    dsColors.orange20,
    dsColors.yellow05,
    dsColors.orange05,
    dsColors.blue20,
    dsColors.cyan05,
    dsColors.orange20,
];
const symbolsToRemove = /\D[^.-]/g;
const reduceNumber = (bigNumber) => bigNumber
    .toString()
    .split('')
    .map(Number)
    .reduce((a, b) => a + b);
const hashUuid = (id) => {
    // substring is used here to get more variety of numbers,
    // without it the numbers were too repetitive resulting in the same bg colors being used next to each other
    let hashedNumber = parseInt(id.substring(2, id.length - 3).replace(symbolsToRemove, ''));
    do {
        hashedNumber = reduceNumber(hashedNumber);
    } while (hashedNumber > 9);
    return hashedNumber;
};
const ContentCardContent = ({ id, image, title, description, imageInCircle = false, eyebrowText, descriptionStartIconUrl, isCompleted, backgroundColor, progressBarValue, showNewContentIndicator = false, }) => {
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('sm', true));
    const TitleComponent = smallScreenDetected ? HeadingText10 : HeadingText30;
    const DescriptionComponent = smallScreenDetected
        ? BodyTextXSmall
        : BodyTextSmall;
    const ImageComponent = imageInCircle ? CircleImage : Image;
    const [bgColorToUse, setBgColorToUse] = useState(backgroundColor);
    useEffect(() => {
        if (!backgroundColor) {
            setBgColorToUse(possibleColors[hashUuid(id)]);
        }
    }, [backgroundColor, id]);
    const progressValue = typeof progressBarValue === 'number' &&
        Math.round(Math.max(0, Math.min(progressBarValue, 1)) * 100);
    return (_jsxs(_Fragment, { children: [_jsxs(ImageContainer, { style: {
                    backgroundColor: bgColorToUse,
                }, children: [isCompleted ? (_jsx(Icon, { "data-testid": ContentCardTestId.Completed, name: "checkmark-enclosed-filled", color: "cyan40" })) : null, image ? _jsx(ImageComponent, { src: image }) : null, typeof progressValue === 'number' ? (_jsx(ProgressBarOuter, { role: "progressbar", "aria-label": "Progress", "aria-valuenow": progressValue, "aria-valuemin": 0, "aria-valuemax": 100, children: _jsx(ProgressBarInner, { value: progressValue }) })) : null] }), _jsxs(ContentWrapper, { children: [eyebrowText ? (_jsx(BodyTextXSmall, { fontColor: "gray65", marginBottom: 4, children: eyebrowText })) : null, _jsxs(TitleWrapper, { children: [showNewContentIndicator ? _jsx(NewContentIndicator, {}) : null, _jsx(TitleComponent, { numberOfLines: 2, marginBottom: 8, textIndent: showNewContentIndicator ? 15 : 0, children: title })] }), _jsxs(DescriptionWrapper, { children: [descriptionStartIconUrl ? (_jsx(DescriptionStartIcon, { alt: "", src: descriptionStartIconUrl })) : null, _jsx(DescriptionComponent, { fontColor: "gray65", marginBottom: 0, children: description })] })] })] }));
};
const Name = 'ContentCard';
export const ContentCard = ({ linkTo, onClick, className, testID, trackClickCallback, ...props }) => {
    if (linkTo) {
        return (_jsx(LinkContainer, { onDragStart: evt => evt.preventDefault(), draggable: false, to: linkTo, className: className, "data-testid": testID, "data-component": Name, onClick: trackClickCallback, children: _jsx(ContentCardContent, { ...props }) }));
    }
    else if (onClick) {
        return (_jsx(ClickableContainer, { onDragStart: evt => evt.preventDefault(), draggable: false, onClick: trackClickCallback
                ? () => {
                    onClick();
                    trackClickCallback();
                }
                : onClick, className: className, "data-testid": testID, "data-component": Name, children: _jsx(ContentCardContent, { ...props }) }));
    }
    else {
        return (_jsx(Container, { onDragStart: evt => evt.preventDefault(), draggable: false, className: className, "data-testid": testID, "data-component": Name, children: _jsx(ContentCardContent, { ...props }) }));
    }
};
export const ContentCardSkeleton = ({ className, }) => {
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('md', true));
    return (_jsxs(Container, { className: className, children: [_jsx(SkeletonPlaceholder, { width: "100%", height: 80, marginBottom: smallScreenDetected ? 8 : 12 }), _jsx(SkeletonPlaceholder, { width: "60%", height: 20, marginBottom: smallScreenDetected ? 4 : 8 }), _jsx(SkeletonPlaceholder, { width: "80%", height: 20 })] }));
};
const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    position: 'relative',
});
const ClickableContainer = styled(Container)({
    cursor: 'pointer',
});
const LinkContainer = Container.withComponent(Link);
const Icon = styled(DsIcon)({
    position: 'absolute',
    width: pxToRem(28),
    height: pxToRem(28),
    left: 'auto',
    right: pxToRem(4),
    top: pxToRem(4),
});
const ImageContainer = styled.div({
    height: pxToRem(160),
    position: 'relative',
    overflow: 'clip',
    borderRadius: pxToRem(4),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
});
const Image = styled.img({
    maxWidth: '100%',
    maxHeight: '100%',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'top',
});
const CircleImage = styled.img({
    width: pxToRem(84),
    height: pxToRem(84),
    borderRadius: '50%',
    [mediaQuery.maxWidth('xl')]: {
        width: pxToRem(68),
        height: pxToRem(68),
    },
});
const ContentWrapper = styled.div({
    position: 'relative',
    marginTop: pxToRem(12),
    [mediaQuery.maxWidth('md')]: {
        marginTop: pxToRem(8),
    },
});
const TitleWrapper = styled.div({
    position: 'relative',
});
const DescriptionWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
});
const DescriptionStartIcon = styled.img({
    marginRight: pxToRem(5),
    width: pxToRem(12),
    height: pxToRem(12),
});
const ProgressBarOuter = styled.div({
    position: 'absolute',
    width: '100%',
    left: 0,
    bottom: 0,
    height: pxToRem(13),
    backgroundColor: dsColors.gray85,
});
const ProgressBarInner = styled.div(({ value = 0 }) => ({
    height: '100%',
    backgroundColor: dsColors.cyan40,
    width: `${value}%`,
}));
const NewContentIndicator = styled.div({
    position: 'absolute',
    backgroundColor: dsColors.cyan40,
    height: pxToRem(10),
    width: pxToRem(10),
    borderRadius: pxToRem(5),
    top: pxToRem(6),
    left: 0,
});
